import { Close, Delete, PhotoCamera, Remove, Upload } from "@mui/icons-material";
import { Avatar, Box, Button, CircularProgress, Divider, Grid2, IconButton, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import api from "../../utils/axios";
import { enqueueSnackbar } from "notistack";
import { useApp } from "../../contexts/AppContext";

const UploadableAvatar = ({ image_url, size, onImageUpload }: { image_url: string | null; size: number; onImageUpload: (data: string | null) => void }) => {
    const [hovered, setHovered] = useState(false);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) return;
        const file = event.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            if (typeof reader.result !== 'string') return
            if (onImageUpload) onImageUpload(reader.result);
        };
    };

    return (
        <Box position="relative" display="inline-block" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} width={size} height={size} >
            <Avatar src={image_url || undefined} alt="User Avatar" sx={{ width: size, height: size, cursor: "pointer" }} />
            {hovered && (
                <>
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        bgcolor="rgba(0, 0, 0, 0.5)"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="50%"
                    >
                        <IconButton component='label' sx={{ color: 'white' }}>
                            <Upload />
                            <input
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={handleFileChange}
                            />
                        </IconButton>
                        {image_url &&
                            <IconButton color="error" onClick={() => onImageUpload(null)}>
                                <Delete />
                            </IconButton>
                        }
                    </Box>
                </>
            )}
        </Box>
    );
};
const BasicInfo = () => {
    const { user, fetchUser } = useAuth();
    const {translate} = useApp()

    const [name, setName] = useState('');
    const [avatar, setAvatar] = useState<string | null>(null);
    const [edited, setEdited] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    useEffect(() => {
        if (!user) return
        setName(user.name)
        setAvatar(user.avatar_url)
    }, [user])

    useEffect(() => {
        if (!user) return
        if (name !== user.name) return setEdited(true)
        if (avatar !== user.avatar_url) return setEdited(true)

        setEdited(false)
    }, [name, avatar, user])

    const handleSubmit = () => {
        if (!user) return
        setUpdating(true)
        if (user.role === 'admin') {
            api.post('/admins/' + user.user_id, {
                name,
                avatar_image: avatar && avatar.startsWith('http') ? undefined : avatar
            }, { meta: { showSnackbar: true } }).catch(console.error).finally(() => fetchUser(() => setUpdating(false)))
        } else if (user.role === 'manager') {
            api.post('/managers/' + user.user_id, {
                name,
                avatar_image: avatar && avatar.startsWith('http') ? undefined : avatar
            }, { meta: { showSnackbar: true } }).catch(console.error).finally(() => fetchUser(() => setUpdating(false)))
        } else if (user.role === 'employee') {
            api.post('/employees/' + user.user_id, {
                name,
                avatar_image: avatar && avatar.startsWith('http') ? undefined : avatar
            }, { meta: { showSnackbar: true } }).catch(console.error).finally(() => fetchUser(() => setUpdating(false)))
        } else if (user.role === 'customer') {
            api.post('/customers/' + user.user_id, {
                name,
                avatar_image: avatar && avatar.startsWith('http') ? undefined : avatar
            }, { meta: { showSnackbar: true } }).catch(console.error).finally(() => fetchUser(() => setUpdating(false)))
        }
    }

    const handleCancel = () => {
        setCancelling(true)
        fetchUser(() => setCancelling(false))
    }

    if (!user) return <CircularProgress />

    return (
        <Grid2 container>
            <Grid2>
                <Grid2 container flexDirection={'column'} gap={2}>
                    <Grid2 display={'flex'} justifyContent={'center'}>
                        <UploadableAvatar image_url={avatar} onImageUpload={(img) => setAvatar(img)} size={150} />
                    </Grid2>
                    <Grid2 container gap={2}>
                        <Grid2>
                            <TextField label={translate('Name')} size="small" value={name} onChange={(e) => setName(e.target.value)} />
                        </Grid2>
                        <Grid2>
                            <TextField disabled label={translate('Email')} size="small" value={user.email} />
                        </Grid2>
                    </Grid2>
                    <Grid2 container gap={1}>
                        <Grid2>
                            <Button startIcon={updating && <CircularProgress size={14} />} disabled={!edited || updating || cancelling} variant="contained" onClick={handleSubmit} sx={{ fontWeight: 'bold', borderWidth: '2px' }}>{translate("Save")}</Button>
                        </Grid2>
                        <Grid2>
                            <Button startIcon={cancelling && <CircularProgress size={14} />} disabled={!edited || updating || cancelling} variant="outlined" onClick={handleCancel} sx={{ fontWeight: 'bold', borderWidth: '2px' }}>{translate("Cancel")}</Button>
                        </Grid2>
                    </Grid2>
                </Grid2>
            </Grid2>
        </Grid2>
    )
}

const PasswordReset = () => {
    const {translate} = useApp()

    const [oldPass, setOldPass] = useState('');
    const [newPass, setNewPass] = useState('');
    const [confirmPass, setConfirmPass] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const submitValid = (oldPass && newPass && confirmPass)

    const clearForm = () => {
        setOldPass('')
        setNewPass('')
        setConfirmPass('')
    }

    const handleSubmit = () => {
        if (newPass !== confirmPass) return enqueueSnackbar('Passwords must match', { variant: 'error' })
        if (newPass === oldPass) return enqueueSnackbar('New password cannot be the same as old one', { variant: 'error' })

        setSubmitting(true)
        api.patch('/auth/resetPassword', { old_password: oldPass, new_password: newPass }, { meta: { showSnackbar: true } })
            .then(clearForm)
            .catch(console.error)
            .finally(() => setSubmitting(false))
    }

    return (
        <Grid2 container gap={2} flexDirection={'column'}>
            <Grid2 container gap={2}>
                <Grid2>
                    <TextField label={translate('Old Password')} type="password" size="small" value={oldPass} onChange={(e) => setOldPass(e.target.value)} />
                </Grid2>
                <Grid2>
                    <TextField label={translate('New Password')} type="password" size="small" value={newPass} onChange={(e) => setNewPass(e.target.value)} />
                </Grid2>
                <Grid2>
                    <TextField label={translate('Confirm Password')} type="password" size="small" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} />
                </Grid2>
            </Grid2>
            <Grid2 container gap={1}>
                <Grid2>
                    <Button disabled={!submitValid || submitting} startIcon={submitting && <CircularProgress size={14} />} variant="contained" onClick={handleSubmit}>{translate("Save")}</Button>
                </Grid2>
                <Grid2>
                    <Button disabled={!submitValid || submitting} variant="outlined" onClick={clearForm} sx={{ fontWeight: 'bold', borderWidth: '2px' }}>{translate("Cancel")}</Button>
                </Grid2>
            </Grid2>
        </Grid2>
    )
}

export default function ProfileSettings() {
    const {translate} = useApp()

    return (
        <Grid2 container flexDirection={'column'} gap={2}>
            <Grid2 container flexDirection={'column'} gap={1}>
                <Grid2>
                    <Typography variant="h6" fontWeight={'bold'}>{translate("Basic Information")}</Typography>
                </Grid2>
                <Grid2>
                    <Divider />
                </Grid2>
            </Grid2>
            <Grid2>
                <BasicInfo />
            </Grid2>
            <Grid2 container flexDirection={'column'} gap={1}>
                <Grid2>
                    <Typography variant="h6" fontWeight={'bold'}>{translate("Update Password")}</Typography>
                </Grid2>
                <Grid2>
                    <Divider />
                </Grid2>
            </Grid2>
            <Grid2>
                <PasswordReset />
            </Grid2>
        </Grid2>
    )
}
